import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from "@mui/material";
import BusinessIcon from "@mui/icons-material/Business";

import { setUser } from "../../redux/slices/userSlice";
import { loginWithCustomToken } from "../../services/auth/AuthServices";
import {
  acceptInvite,
  previewInvite,
  sendInviteCode,
  verifyInviteCode,
} from "../../services/backend/EntityAuthService";
import { getEntityConfig } from "../../services/backend/EntityConfigService";
import {
  clearRecaptcha,
  confirmPhoneCode,
  isValidPhone,
  phoneAuthErrorMessage,
  sendPhoneCode,
  toE164,
} from "../../services/auth/PhoneVerification";
import { NO_SMS_HINT, phoneSendInfo, useResendCooldown } from "../submissions/phoneGate";
import PhoneNumberField from "../generic/PhoneNumberField";
import { DEFAULT_PHONE_COUNTRY } from "../../phoneCountries";

// Accepting an invitation to join a company's staff.
//
// Public: whoever lands here has no account yet — creating it is precisely the
// end of this flow. The link carries an HMAC token the backend verifies at every
// step; the email comes from there and not from the form, so the invitation
// cannot be diverted to another address.
//
// Both verifications are required before anything is created: the code to the
// email and the SMS to the mobile. It is the same thing a company is asked for
// when registering, and for the same reason: an account that can see a company's
// paperwork should not be born from a link forwarded over chat.

const RECAPTCHA_ID = "recaptcha-invite-accept";

const STEPS = ["Tus datos", "Tu correo", "Tu celular"];

export default function InviteAccept() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("t") || "";

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [fatal, setFatal] = useState(null);

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const [form, setForm] = useState({
    name: "", lastName: "", nuip: "", position: "",
  });
  const [code, setCode] = useState("");
  const [emailDone, setEmailDone] = useState(false);

  // PhoneNumberField handles the dialling code and the number separately and
  // emits the composed E.164; that is what gets stored, and what
  // toE164/sendPhoneCode consume.
  const [phone, setPhone] = useState({
    countryCode: DEFAULT_PHONE_COUNTRY, national: "", e164: "",
  });
  const [confirmation, setConfirmation] = useState(null);
  const [smsCode, setSmsCode] = useState("");
  const [cfg, setCfg] = useState({});
  const cooldown = useResendCooldown();

  useEffect(() => {
    (async () => {
      if (!token) { setFatal("El enlace no es válido."); setLoading(false); return; }
      const [result, config] = await Promise.all([previewInvite(token), getEntityConfig()]);
      setCfg(config || {});
      if (!result.ok) setFatal(result.error);
      else setInvite(result.invite);
      setLoading(false);
    })();
    return () => clearRecaptcha();
  }, [token]);

  const profileValid = useMemo(
    () => form.name.trim() && form.lastName.trim() && form.nuip.trim(),
    [form]
  );

  const requestEmailCode = useCallback(async () => {
    setBusy(true); setError(null);
    const sent = await sendInviteCode(token);
    setBusy(false);
    if (!sent.ok) { setError(sent.error); return false; }
    setInfo(`Te enviamos un código a ${invite.email}.`);
    return true;
  }, [token, invite]);

  const goToEmailStep = async () => {
    if (await requestEmailCode()) setStep(1);
  };

  const submitEmailCode = async () => {
    setBusy(true); setError(null);
    const result = await verifyInviteCode(token, code.trim());
    setBusy(false);
    if (!result.ok) { setError(result.error); return; }
    setEmailDone(true);
    setInfo(null);
    setStep(2);
  };

  const requestSms = async () => {
    const e164 = toE164(phone.e164);
    if (!isValidPhone(e164)) { setError("El número de celular no es válido."); return; }
    setBusy(true); setError(null);
    try {
      const result = await sendPhoneCode(e164, RECAPTCHA_ID, { testMode: !!cfg.smsTestMode });
      setConfirmation(result);
      setInfo(phoneSendInfo(e164, !!cfg.smsTestMode));
      cooldown.start();
    } catch (err) {
      setError(phoneAuthErrorMessage(err));
    }
    setBusy(false);
  };

  // The last step does three things at once: confirms the SMS, trades both
  // proofs for an account, and signs in. Deliberately chained — the phone ID
  // token is single-use and short-lived, so it cannot sit waiting for the user
  // to press another button.
  const finish = async () => {
    setBusy(true); setError(null);
    let phoneToken;
    try {
      const confirmed = await confirmPhoneCode(confirmation, smsCode);
      phoneToken = confirmed.idToken;
    } catch (err) {
      setBusy(false);
      setError(phoneAuthErrorMessage(err));
      return;
    }

    const accepted = await acceptInvite({
      token,
      profile: { ...form, phone: toE164(phone.e164) },
      phoneToken,
    });
    if (!accepted.ok) {
      setBusy(false);
      setError(accepted.error);
      return;
    }

    const session = await loginWithCustomToken(accepted.customToken, true);
    setBusy(false);
    if (!session || !session.user) {
      // The account was created; what failed was opening the session. Send them
      // to the login, where they can get in with their code: retrying the
      // invitation would not work because it is consumed.
      setError("Tu cuenta quedó creada, pero no pudimos iniciar tu sesión. Ingresa desde el login.");
      return;
    }
    dispatch(setUser(session.user));
    // Home, not /my-entity: it is where everyone lands after signing in, and for
    // a company account the «Mi Entidad» tile is right there — the only one on
    // the screen. Dropping straight into the record skipped the greeting that
    // tells them whose company they just joined.
    navigate("/home");
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (fatal) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" gutterBottom>No pudimos abrir la invitación</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>{fatal}</Typography>
          <Button variant="contained" onClick={() => navigate("/login")} sx={{ textTransform: "none" }}>
            Ir al inicio de sesión
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Paper sx={{ p: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
          <BusinessIcon color="primary" />
          <Typography variant="h6">{invite.entityLegalName || "Tu empresa"}</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Te invitaron a hacer parte de esta empresa con el correo <strong>{invite.email}</strong>.
          Para terminar, confirma tus datos, tu correo y tu celular.
        </Typography>

        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {!error && info && <Alert severity="info" sx={{ mb: 2 }}>{info}</Alert>}

        {step === 0 && (
          <Stack spacing={2}>
            <TextField
              label="Nombres" required fullWidth value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <TextField
              label="Apellidos" required fullWidth value={form.lastName}
              onChange={(e) => setForm({ ...form, lastName: e.target.value })}
            />
            <TextField
              label="Documento de identidad" required fullWidth value={form.nuip}
              onChange={(e) => setForm({ ...form, nuip: e.target.value })}
            />
            <TextField
              label="Cargo en la empresa" fullWidth value={form.position}
              onChange={(e) => setForm({ ...form, position: e.target.value })}
            />
            <Button
              variant="contained" onClick={goToEmailStep}
              disabled={busy || !profileValid} sx={{ textTransform: "none" }}
            >
              {busy ? <CircularProgress size={24} /> : "Continuar"}
            </Button>
          </Stack>
        )}

        {step === 1 && (
          <Stack spacing={2}>
            <Typography variant="body2">
              Escribe el código de 6 dígitos que enviamos a <strong>{invite.email}</strong>.
            </Typography>
            <TextField
              label="Código" fullWidth value={code} autoFocus
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputProps={{ inputMode: "numeric", maxLength: 6 }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained" onClick={submitEmailCode}
                disabled={busy || code.length !== 6} sx={{ textTransform: "none" }}
              >
                {busy ? <CircularProgress size={24} /> : "Verificar"}
              </Button>
              <Button onClick={requestEmailCode} disabled={busy} sx={{ textTransform: "none" }}>
                Reenviar
              </Button>
            </Stack>
          </Stack>
        )}

        {step === 2 && (
          <Stack spacing={2}>
            {emailDone && <Alert severity="success">Correo verificado.</Alert>}
            <Typography variant="body2">
              Por último, verifica tu número de celular.
            </Typography>
            <PhoneNumberField
              required
              label="Celular"
              countryCode={phone.countryCode}
              national={phone.national}
              onChange={setPhone}
              disabled={!!confirmation || busy}
            />
            {!confirmation ? (
              <Button
                variant="contained" onClick={requestSms}
                disabled={busy || !phone.national} sx={{ textTransform: "none" }}
              >
                {busy ? <CircularProgress size={24} /> : "Enviarme el código"}
              </Button>
            ) : (
              <>
                <TextField
                  label="Código del SMS" fullWidth value={smsCode} autoFocus
                  onChange={(e) => setSmsCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  inputProps={{ inputMode: "numeric", maxLength: 6 }}
                  helperText={NO_SMS_HINT}
                />
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained" onClick={finish}
                    disabled={busy || smsCode.length < 4} sx={{ textTransform: "none" }}
                  >
                    {busy ? <CircularProgress size={24} /> : "Aceptar invitación"}
                  </Button>
                  <Button
                    onClick={requestSms}
                    disabled={busy || cooldown.seconds > 0}
                    sx={{ textTransform: "none" }}
                  >
                    {cooldown.seconds > 0 ? `Reenviar (${cooldown.seconds}s)` : "Reenviar"}
                  </Button>
                </Stack>
              </>
            )}
            {/* Container for Firebase Phone Auth's invisible reCAPTCHA. */}
            <div id={RECAPTCHA_ID} />
          </Stack>
        )}
      </Paper>
    </Container>
  );
}
